import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout/Layout";
import { config, manageInvoiceAPI, purchaseOrderAPI } from "../../config";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DataTable from "react-data-table-component";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import moment from "moment/moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { MaterialReactTable } from "material-react-table";

function InvoiceList() {
  const navigate = useNavigate();

  const [totalCount, setTotalCount] = useState(0);
  const [clientMasterList, setClientMasterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnName, setColumnName] = useState(0);
  const [sortDirection, setSortDirection] = useState(0);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);

  let tempRefs = useRef(false);

  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1);
    }
  }, []);

  const fetchUsers = async (
    page,
    rowsPerPage,
    columnName = "",
    sortDirection = ""
  ) => {
    setLoading(true);
    setClientMasterList([]);

    const data = JSON.stringify({
      txtColumnName: columnName,
      txtSortDirection: sortDirection,
      txtInvoiceType: orderType,
      intLimit: pageSize,
      intOffset: pageIndex * pageSize,
      intType: 16,
      sorting,
      columnFilters: filters,
    });

    config.method = "POST";
    config.data = data;
    config.url = manageInvoiceAPI;

    axios(config)
      .then(function (response) {
        setClientMasterList(response.data.data);
        setTotalCount(response.data.total_count);
        setLoading(false); // Set loading to false after data is received
      })
      .catch(function (error) {
        console.log(error);
        setLoading(false); // Set loading to false in case of error
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [pageIndex, pageSize, filters, sorting]);

  const [orderType, setOrderType] = useState(0);

  const [ioTypeList, setIoTypeList] = useState([
    { label: "All", value: 0 },
    { label: "Invoice", value: 1 },
    { label: "Proforma", value: 2 },
    { label: "Credit Note", value: 3 },
  ]);

  function handleInvoiceType(invoiceType) {
    if (invoiceType == 1) {
      return "Invoice";
    } else if (invoiceType == 2) {
      return "Proforma";
    } else if (invoiceType == 3) {
      return "Credit Note";
    }
  }

  const handleEditRole = (id) => {
    navigate(`/invoice/view/${id}`);
  };

  const handleEditInvoice = (id) => {
    navigate(`/invoice/edit/${id}`);
  };

  const twoDecimal = (x) => {
    return Number.parseFloat(x).toFixed(2);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "invoice_no",
        header: "Invoice No.",
        enableSorting: true,
        enableColumnFilter: true,
        size: 100, // ~10% width
      },
      {
        accessorKey: "company_name",
        header: "Company Name",
        enableSorting: true,
        enableColumnFilter: true,
        size: 160, // ~16% width
      },
      {
        id: "createdAt",
        header: "Created At",
        enableSorting: true,
        enableColumnFilter: false,
        size: 170,
        accessorFn: (row) =>
          row.createdAt
            ? moment(row.createdAt)
                .subtract(2.3, "hours")
                .format("DD-MM-YYYY HH:mm a")
            : "-",
      },
      {
        id: "invoice_type",
        header: "Invoice Type",
        enableSorting: true,
        enableColumnFilter: true,
        accessorFn: (row) => handleInvoiceType(row.invoice_type),
      },
      {
        accessorKey: "po_ref_no",
        header: "P.O ref. No.",
        enableSorting: false,
        enableColumnFilter: true,
        size: 150,
      },
      {
        accessorKey: "project_name",
        header: "Project Name",
        enableSorting: true,
        enableColumnFilter: true,
        size: 180,
      },
      {
        accessorKey: "hsn_sac",
        header: "HSN/SAC",
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
      },
      {
        accessorKey: "total_with_gst",
        header: "Total Amount with GST",
        enableSorting: false,
        enableColumnFilter: false,
        size: 150,
        accessorFn: (row) => twoDecimal(row.total_with_gst),
      },
      {
        id: "view-action",
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            color="success"
            onClick={() => handleEditRole(row.original.invoiceId)}
            aria-label="view"
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
      {
        id: "edit-action",
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            color="warning"
            onClick={() => handleEditInvoice(row.original.invoiceId)}
            aria-label="edit"
          >
            <EditLocationOutlinedIcon />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Invoice List
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Invoice</Typography>
              <Typography color="text.primary">List</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    select
                    label="Invoice Type"
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                  >
                    {ioTypeList.map((row, index) => (
                      <MenuItem value={row.value} key={index}>
                        {row.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={6}
                spacing={1}
                justifyContent="flex-start"
                alignItems={`center`}
              >
                <Grid item>
                  <LoadingButton
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      tempRefs.current = false;
                      fetchUsers(1);
                    }}
                  >
                    Search
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <LoadingButton
                  size="medium"
                  variant="contained"
                  color="success"
                  className="button_first"
                  href="/invoice/create"
                  startIcon={<AddIcon />}
                >
                  Add Invoice
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={clientMasterList}
          enableGlobalFilter={false}
          enableColumnFilters={true}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters: filters,
          }}
          manualPagination
          manualSorting
          manualFiltering
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });
            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important", // selected text color
              },
              "& .MuiSelect-icon": {
                color: "#fff !important", // dropdown arrow color
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default InvoiceList;
